import { Component, effect } from '@angular/core';
import { PositionData } from '@interfaces/open-positions.model';
import { paginationParams } from '@shared/config/table.config';
import { OpenPositionsService } from './open-positions.service';
import { GlobalErrorService } from '@services/global-error.service';
import { SearchService } from '@services/search.service';
import { ChangeDetectorRef } from '@angular/core';
import { CandidatesService } from '../candidates/candidates.service';
import { Candidates } from '@interfaces/candidates.model';
import { ActivatedRoute, Router } from '@angular/router';
import { PersonaType, Priority, TabLabels, VendorManagementEnum } from '@constants/global.enum';
import { mapResponse } from './open-positions.helper';
import { FileDownloadService } from '@services/file-download.service';
import { dateFormat } from '@constants/dateFormat';
import { DEFAULT_LEGEND_ITEMS } from '@constants/constants';
import { truncateText } from '@utils/utils';
import { VendorOpenPositionsQuery } from './open-positions.service';

@Component({
  selector: 'app-open-positions',
  templateUrl: './open-positions.component.html',
  styleUrls: ['./open-positions.component.scss'],
})
export class OpenPositionsComponent {
  VendorManagementEnum = VendorManagementEnum;
  vendorData: PositionData[] = [];
  displayData: PositionData[] = [];
  // custom tab state (replaced emerald-tab-bar so the badge counts are plain
  // interpolation bindings — no MDC re-activation / NG0100 badge quirks).
  tabLabel = TabLabels;
  activeTab: string = TabLabels.OpenPositions;
  positionCount = 0;
  candidatesCount = 0;
  // server-driven filter + paging state
  facets: { [key: string]: string[] } = {};
  activeFilters: VendorOpenPositionsQuery = {};
  pageSize = paginationParams.pageSize;
  sortColumn = '';
  sortDir: 'asc' | 'desc' = 'asc';
  // dedicated filter panel (native controls, driven by server facets)
  showFilterPanel = false;
  statusOptions = ['Open', 'Onhold', 'Closed'];
  filterDraft = {
    status: ['Open'] as string[],
    lob: '',
    leader: '',
    location: '',
    jobTitle: '',
    openedDate: '',
    desiredStartDate: '',
  };
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  fieldsVisibility: string = PersonaType.vendorRepOpenPos;
  imageSrc: string = 'assets/img/no-data-found.png';
  displayCandidatesData: Candidates[] = [];
  filterValue: string = '';
  edit = false;
  candidateId: string = '';
  jobTitle: string = '';
  location: string = '';
  isDataLoaded: boolean = false;
  priority = Priority;
  dateFormat = dateFormat;
  columnTextDisplayLimit = 10;
  // only the critical marker is shown as a flag on the grid, so drop "Standard"
  legendItems = DEFAULT_LEGEND_ITEMS.filter((item) => item.label === Priority.Critical);
  public truncateText = truncateText;
  showJobDetails = false;
  selectedJob: PositionData | null = null;
  // fixed-position hover tooltip for truncated cells (escapes table clipping)
  hoverTip = { show: false, text: '', x: 0, y: 0 };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private openPositionsService: OpenPositionsService,
    private globalError: GlobalErrorService,
    private searchService: SearchService,
    private cdr: ChangeDetectorRef,
    private candidatesService: CandidatesService,
    private fileDownloadService: FileDownloadService,
  ) {
    effect(() => {
      const signalData = this.candidatesService.candidatesSignal();
      const totalRecords = signalData?.data?.totalRecords;
      if (totalRecords !== undefined) {
        this.candidatesCount = totalRecords;
        this.cdr.markForCheck();
      }
    });
  }

  ngOnInit() {
    // deep link from the dashboard Interview Status KPI: ?tab=candidates
    const tabParam = this.route.snapshot.queryParamMap.get('tab');
    this.activeTab =
      tabParam?.toLowerCase() === 'candidates'
        ? this.tabLabel.Candidates
        : this.tabLabel.OpenPositions;
    this.activeFilters = { status: 'Open', page: 1, limit: this.pageSize };
    this.loadPage();
    this.getCandidateCount();
  }

  selectTab(label: string): void {
    if (this.activeTab === label) return;
    this.activeTab = label;
    // Open positions is inline (the parent isn't destroyed on tab switch like
    // <app-candidates> is), so refetch on entry to show the latest data.
    if (label === this.tabLabel.OpenPositions) {
      this.loadPage();
    }
  }

  /** Server-side sort: toggle direction on the same column, else sort the new
   *  column ascending. Resets to page 1 and refetches. */
  sortBy(field: string): void {
    if (this.sortColumn === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = field;
      this.sortDir = 'asc';
    }
    this.activeFilters = {
      ...this.activeFilters,
      sort: this.sortColumn,
      dir: this.sortDir,
      page: 1,
    };
    this.loadPage();
  }

  sortIndicator(field: string): string {
    return this.sortColumn === field ? this.sortDir : '';
  }

  get avgProfilesPerPosition(): string {
    if (!this.positionCount) return '0';
    return (this.candidatesCount / this.positionCount).toFixed(1);
  }

  /** Fetch the current page from the server with the active status + filters.
   *  Vendor scope is applied server-side from the JWT. */
  private loadPage(): void {
    // Clear the table while fetching — a loading message shows instead of stale rows.
    this.isDataLoaded = false;
    this.displayData = [];
    this.vendorData = [];
    this.cdr.markForCheck();
    this.openPositionsService.getVendorPositions(this.activeFilters).subscribe((payload) => {
      const total = payload ? payload.totalRecords : 0;
      if (payload) {
        this.vendorData = (payload.data || []).map(mapResponse);
        this.displayData = this.vendorData;
        this.facets = payload.facets || {};
      } else {
        this.vendorData = [];
        this.displayData = [];
        this.facets = {};
      }
      this.positionCount = total;
      this.isDataLoaded = true;
      this.cdr.markForCheck();
    });
  }

  // ---- dedicated filter panel -------------------------------------------
  get facetLob(): string[] { return this.facets['lob'] || []; }
  get facetLeader(): string[] { return this.facets['leader'] || []; }
  get facetLocation(): string[] { return this.facets['location'] || []; }
  get facetJobTitle(): string[] { return this.facets['jobTitle'] || []; }

  get activeFilterCount(): number {
    const f = this.activeFilters;
    let n = 0;
    if (f.lob) n++;
    if (f.leader) n++;
    if (f.location) n++;
    if (f.jobTitle) n++;
    if (f.openedDate) n++;
    if (f.desiredStartDate) n++;
    if (f.status && f.status !== 'Open') n++;
    return n;
  }

  toggleFilterPanel(): void {
    if (!this.showFilterPanel) {
      // sync the draft from the currently applied filters
      this.filterDraft = {
        status: (this.activeFilters.status || 'Open').split(',').filter(Boolean),
        lob: this.activeFilters.lob || '',
        leader: this.activeFilters.leader || '',
        location: this.activeFilters.location || '',
        jobTitle: this.activeFilters.jobTitle || '',
        openedDate: this.activeFilters.openedDate || '',
        desiredStartDate: this.activeFilters.desiredStartDate || '',
      };
    }
    this.showFilterPanel = !this.showFilterPanel;
  }

  toggleStatus(status: string): void {
    const i = this.filterDraft.status.indexOf(status);
    if (i >= 0) this.filterDraft.status.splice(i, 1);
    else this.filterDraft.status.push(status);
  }

  applyFilterPanel(): void {
    const d = this.filterDraft;
    this.activeFilters = {
      ...this.activeFilters,
      status: d.status.length ? d.status.join(',') : 'Open',
      lob: d.lob || undefined,
      leader: d.leader || undefined,
      location: d.location || undefined,
      jobTitle: d.jobTitle || undefined,
      openedDate: d.openedDate || undefined,
      desiredStartDate: d.desiredStartDate || undefined,
      page: 1,
    };
    this.showFilterPanel = false;
    this.loadPage();
  }

  resetFilterPanel(): void {
    this.filterDraft = {
      status: ['Open'], lob: '', leader: '', location: '',
      jobTitle: '', openedDate: '', desiredStartDate: '',
    };
    this.activeFilters = {
      status: 'Open',
      page: 1,
      limit: this.pageSizeValue,
      search: this.activeFilters.search,
      sort: this.activeFilters.sort,
      dir: this.activeFilters.dir,
    };
    this.showFilterPanel = false;
    this.loadPage();
  }

  // ---- server-side pagination ---------------------------------------------
  get currentPageValue(): number {
    return this.activeFilters.page ?? 1;
  }

  get pageSizeValue(): number {
    return this.activeFilters.limit ?? this.pageSize;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil((this.positionCount || 0) / this.pageSizeValue));
  }

  get rangeStart(): number {
    return this.positionCount === 0 ? 0 : (this.currentPageValue - 1) * this.pageSizeValue + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPageValue * this.pageSizeValue, this.positionCount);
  }

  /** Page numbers to render; -1 is an ellipsis gap. */
  get pageWindow(): number[] {
    const total = this.totalPages;
    const cur = this.currentPageValue;
    const out: number[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) out.push(i);
      return out;
    }
    out.push(1);
    const start = Math.max(2, cur - 1);
    const end = Math.min(total - 1, cur + 1);
    if (start > 2) out.push(-1);
    for (let i = start; i <= end; i++) out.push(i);
    if (end < total - 1) out.push(-1);
    out.push(total);
    return out;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPageValue) return;
    this.activeFilters = { ...this.activeFilters, page };
    this.loadPage();
  }

  prevPage(): void {
    this.goToPage(this.currentPageValue - 1);
  }

  nextPage(): void {
    this.goToPage(this.currentPageValue + 1);
  }

  onPageSizeChange(event: Event): void {
    const size = +(event.target as HTMLSelectElement).value;
    this.activeFilters = { ...this.activeFilters, limit: size, page: 1 };
    this.loadPage();
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ---- summary + cell helpers ---------------------------------------------
  get lobCount(): number {
    return this.facets['lob']?.length || 0;
  }

  get locationCount(): number {
    return this.facets['location']?.length || 0;
  }

  get hasActiveFilters(): boolean {
    const f = this.activeFilters;
    return !!(f.status || f.lob || f.leader || f.location || f.search);
  }

  initials(name?: string | null): string {
    if (!name) return '—';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '—';
  }

  isRowPriority(row: PositionData): boolean {
    return row?.isPriority?.toString() === 'true';
  }

  agingClass(age?: number): string {
    const a = +(age || 0);
    return a > 90 ? 'stale' : a >= 30 ? 'mod' : 'fresh';
  }

  agingWidth(age?: number): number {
    const a = +(age || 0);
    return Math.max(6, Math.min(100, Math.round((a / 180) * 100)));
  }

  statusClass(status?: string): string {
    const v = (status || '').toLowerCase();
    if (v === 'open') return 'open';
    if (v === 'onhold' || v === 'on hold') return 'hold';
    return 'closed';
  }

  getCandidateInfor(
    hiringRequestId: string | number,
    jobTitle: string,
    location: string | null,
  ) {
    this.edit = true;
    this.candidateId = String(hiringRequestId ?? '');
    this.jobTitle = jobTitle;
    this.location = location ?? '';
  }

  resetActions(_event: boolean) {
    this.edit = false;
  }

  receiveData(_data: any, _candidateEmailId: string) {
    this.displayData.forEach((_item) => {});
  }

  getCandidateCount() {
    this.candidatesService.getCandidates({ page: 1, limit: 10 });
  }

  getShortText(comment: string): string {
    return truncateText(comment, this.columnTextDisplayLimit);
  }

  searchItem(event: KeyboardEvent) {
    this.filterValue = this.searchService.getFilteredValue(event);
    // debounce server search so we don't refetch on every keystroke
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.activeFilters = {
        ...this.activeFilters,
        search: this.filterValue?.trim() || undefined,
        page: 1,
      };
      this.loadPage();
    }, 400);
  }

  clearInput() {
    this.filterValue = '';
    this.activeFilters = { ...this.activeFilters, search: undefined, page: 1 };
    this.loadPage();
  }

  downloadJobDescription(jobTitleName: string, jobDescriptionPath: string) {
    const fullFileName = jobTitleName + '.pdf';
    this.fileDownloadService.downloadFile(jobDescriptionPath, fullFileName);
  }

  navigateToJobDetails(data: PositionData) {
    this.selectedJob = data;
    this.showJobDetails = true;
  }

  /** Show the fixed tooltip only when the cell text is actually truncated. */
  showTip(event: MouseEvent, text?: string | null): void {
    if (!text) return;
    const cell = event.currentTarget as HTMLElement;
    const inner = (cell.querySelector('.op-tip-txt') as HTMLElement) || cell;
    if (inner.scrollWidth <= inner.clientWidth + 1) return; // not truncated
    const rect = cell.getBoundingClientRect();
    this.hoverTip = { show: true, text, x: rect.left, y: rect.bottom + 4 };
  }

  hideTip(): void {
    this.hoverTip.show = false;
  }

  navigateToVendorDashboard(): void {
    this.router.navigate(['/vendorflow-dashboard']);
  }
}
